<template>
    <div class="small-direct-record">
        <div v-if="step === 1">
            <h2 class="direct-record-title mb-2 text-center">{{ $t('sign_feedback.direct_record.title') }}</h2>
            <p class="direct-record-subtitle mb-3 text-center">{{ $t('sign_feedback.direct_record.mobile.subtitle') }}</p>
            <DirectRecordTips />
            <div class="mt-3">
                <Button class="w-100 button-center typography-md" :label="'Ik ben klaar'" @click="goToStep2" />
            </div>
        </div>

        <div v-else>
            <h2 class="direct-record-title mb-1 text-center">{{ $t('sign_feedback.direct_record.title') }}</h2>
            <p class="direct-record-subtitle mb-3 text-center">{{ $t('sign_feedback.direct_record.subtitle') }}</p>

            <DirectRecordStage
                ref="stage"
                :holistic="holistic"
                :skipPositioningCheck="skipPositioningCheck"
                @postprocessed="onPostProcessed"
                @state-change="onStageStateChange"
            />

            <div class="btn-stack mt-3">
                <Button v-if="firstRecordingDone && !saveLandmarks && !countdown"
                        type="button"
                        class="w-100 mb-2 button-center typography-md"
                        :disabled="busy || !mediaPipeIsLoaded || isPostProcessing || !postProcessingDone"
                        :loading="busy"
                        label="sign_feedback.direct_record.search_sign"
                        :icon="['fas', 'robot']"
                        @click="sendRecording"></Button>
                <Button type="button"
                        class="w-100 mb-2 button-center typography-md"
                        :disabled="busy || !mediaPipeIsLoaded || !holisticPrewarmed || saveLandmarks || countdown || isCheckingPosition || isPostProcessing || (recordedVideoUrl && !recordedReplayReady)"
                        :loading="!mediaPipeIsLoaded || !holisticPrewarmed"
                        :label="getRecordButtonLabel()"
                        :icon="getRecordButtonIcon()"
                        :variant="getRecordButtonVariant()"
                        @click="handleStartRecording"></Button>
                <Button v-if="!firstRecordingDone" class="w-100 button-center link-button" :label="'Ga terug'" :icon="['fas','arrow-left']" :variant="'secondary'" @click="goToStep1" />
            </div>
            
            <!-- AI Disclaimer -->
            <div class="mt-2">
                <div class="ai-disclaimer-text mb-3">
                    {{ $t('sign_feedback.direct_record.ai_disclaimer') }}
                </div>
                <div class="privacy-info-text" v-html="$t('sign_feedback.direct_record.privacy_info')">
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import Button from '@/components/general/Button.vue';
import DirectRecordTips from '@/components/signFeedback/DirectRecordTips.vue';
import DirectRecordStage from '@/components/signFeedback/DirectRecordStage.vue';

@Component({ components: { Button, DirectRecordTips, DirectRecordStage } })
export default class SmallDirectRecordSign extends Vue {
    @Prop() holistic: any;
    @Prop({ type: Boolean, default: false }) skipPositioningCheck!: boolean;
    @Prop({ type: Boolean, default: false }) busy!: boolean;

    public step = 1;

    @Emit('nextStep')
    public sendRecording() {
        return this.postProcessedLandmarks;
    }

    public goToStep2() {
        this.step = 2;
    }

    public goToStep1() {
        const stage: any = this.$refs['stage'];
        if (stage && typeof stage.teardown === 'function') {
            try { stage.teardown(); } catch (e) { /* noop */ }
        }
        this.step = 1;
        this.mediaPipeIsLoaded = false;
        this.saveLandmarks = false;
        this.countdown = false;
        this.isCheckingPosition = false;
        this.isPostProcessing = false;
        this.postProcessingDone = false;
        this.recordedVideoUrl = null;
        this.recordedReplayReady = false;
    }

    public mediaPipeIsLoaded = false;
    public saveLandmarks = false;
    public firstRecordingDone = false;
    public countdown = false;
    public isCheckingPosition = false;
    public isPostProcessing = false;
    public postProcessingDone = false;
    public recordedVideoUrl: string | null = null;
    public recordedReplayReady = false;
    public holisticPrewarmed = false;
    public postProcessedLandmarks: any[] = [];

    private getRecordButtonLabel(): string {
        if (this.countdown || this.saveLandmarks) {
            return 'sign_feedback.direct_record.recording';
        }
        if (this.isCheckingPosition) {
            return 'sign_feedback.direct_record.positioning.checking';
        }
        if (this.firstRecordingDone) {
            return 'sign_feedback.step2.restart_recording';
        }
        return 'sign_feedback.direct_record.start_recording';
    }

    private getRecordButtonIcon(): string[] {
        if (this.isCheckingPosition) {
            return ['fas', 'search'];
        }
        if (this.firstRecordingDone) {
            return ['fas', 'repeat'];
        }
        return ['fas', 'circle'];
    }

    private getRecordButtonVariant(): 'primary' | 'secondary' {
        if (this.isCheckingPosition) {
            return 'secondary';
        }
        if (this.firstRecordingDone && !this.countdown && !this.saveLandmarks) {
            return 'secondary';
        }
        return 'primary';
    }

    public handleStartRecording() {
        const stage: any = this.$refs['stage'];
        if (stage && typeof stage.startRecording === 'function') {
            stage.startRecording();
        }
    }

    public onPostProcessed(frames: any[]) {
        this.postProcessedLandmarks = frames || [];
    }

    public onStageStateChange(payload: any) {
        this.mediaPipeIsLoaded = !!payload.mediaPipeIsLoaded;
        this.holisticPrewarmed = !!payload.holisticPrewarmed;
        this.saveLandmarks = !!payload.saveLandmarks;
        this.firstRecordingDone = !!payload.firstRecordingDone;
        this.countdown = !!payload.countdown;
        this.isCheckingPosition = !!payload.isCheckingPosition;
        this.isPostProcessing = !!payload.isPostProcessing;
        this.postProcessingDone = !!payload.postProcessingDone;
        this.recordedVideoUrl = payload.recordedVideoUrl || null;
        this.recordedReplayReady = !!payload.recordedReplayReady;
    }

    public teardown() {
        const stage: any = this.$refs['stage'];
        if (stage && typeof stage.teardown === 'function') {
            try { stage.teardown(); } catch (e) { /* noop */ }
        }
    }

    public beforeDestroy() {
        const stage: any = this.$refs['stage'];
        if (stage && typeof stage.teardown === 'function') {
            try { stage.teardown(); } catch (e) { /* noop */ }
        }
    }
}
</script>

<style lang="scss" scoped>
.direct-record-title {
    color: #273F7A;
    font-family: 'Raleway', sans-serif;
    font-size: 20px;
    font-weight: 700;
}

.direct-record-subtitle {
    color: #666060;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 400;
}

.btn-box {
    margin-top: 1rem;
}

.btn-stack {
    display: flex;
    flex-direction: column;
}

.button-center {
    justify-content: center;
}

.link-button {
    &:hover {
        background-color: transparent !important;
        color: $accent-color !important;
    }
}

.typography-md {
    font-family: 'Roboto', sans-serif;
    font-weight: 500;
    font-size: 16px;
    text-transform: none;
}

.ai-disclaimer-text {
    font-size: 13px;
    color: #6c757d;
    line-height: 1.5;
    margin-bottom: 0.75rem;
}

.privacy-info-text {
    font-size: 12px;
    color: #6c757d;
    line-height: 1.4;
}

.privacy-info-text a {
    color: #007bff;
    text-decoration: underline;
}

.privacy-info-text a:hover {
    color: #0056b3;
    text-decoration: underline;
}
</style>


